import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import type { HierarchyLevelDto } from '@/ApiClient.ts';
import { computed } from 'vue';
import { HierarchyLevelType } from '@/ApiClient.ts';

export const useHierarchyLevels = defineStore('hierarchy-levels', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.hierarchyLevels_GetHierarchyLevels,
    dto: 'HierarchyLevelDto',
    create: api.hierarchyLevels_CreateHierarchyLevel,
    update: api.hierarchyLevels_UpdateHierarchyLevel,
    delete: api.hierarchyLevels_DeleteHierarchyLevel,
    signalR: 'HierarchyLevelsUpdate',
  });

  const topLevel = computed(() => globalStoreBase.loaded.value[0]);

  const primaryLevel = computed(() => globalStoreBase.loaded.value.find((l) => l.type === HierarchyLevelType.Primary));
  const secondaryLevel = computed(() =>
    globalStoreBase.loaded.value.find((l) => l.type === HierarchyLevelType.Secondary)
  );

  function byType(type: HierarchyLevelType) {
    return globalStoreBase.loaded.value.filter((l) => l.type === type);
  }

  function nextLevel(level: HierarchyLevelDto) {
    const index = globalStoreBase.loaded.value.findIndex((l) => l.id === level.id);
    return globalStoreBase.loaded.value[index + 1];
  }

  function previousLevel(level: HierarchyLevelDto) {
    const index = globalStoreBase.loaded.value.findIndex((l) => l.id === level.id);
    return globalStoreBase.loaded.value[index - 1];
  }

  function getOrder(level: HierarchyLevelDto) {
    const index = globalStoreBase.loaded.value.findIndex((l) => l.id === level.id);
    return index + 1;
  }

  function nextVisibleLevel(level: HierarchyLevelDto) {
    const next = nextLevel(level);

    if (!next) return undefined;

    if (next.type !== HierarchyLevelType.Hidden) return next;

    return nextVisibleLevel(next);
  }

  function getGroupingTypeForLevel(level: HierarchyLevelDto) {
    if (level.type === HierarchyLevelType.Primary) {
      return HierarchyLevelType.PrimaryGroup;
    }

    if (level.type === HierarchyLevelType.Secondary) {
      return HierarchyLevelType.SecondaryGroup;
    }

    return undefined;
  }

  return {
    ...globalStoreBase,
    byType,
    topLevel,
    primaryLevel,
    secondaryLevel,
    nextLevel,
    getGroupingTypeForLevel,
    previousLevel,
    getOrder,
    nextVisibleLevel,
  };
});
